// Enhanced JSON-LD Schema generators for comprehensive SEO

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Daniel Albert Law Firm",
  "alternateName": ["Daniel Albert Law", "Daniel Albert Immigration Lawyer"],
  "url": "https://danielalbertlaw.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://danielalbertlaw.com/search?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  },
  "inLanguage": ["en-US", "es-ES", "vi-VN"]
};

export const organizationSchemaEnhanced = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  "@id": "https://danielalbertlaw.com/#organization",
  "name": "Daniel Albert Law Firm",
  "alternateName": "Daniel Albert Law",
  "description": "Leading Houston law firm specializing in Immigration, Criminal Defense, Personal Injury, Family Law, Business Law, Real Estate, Tax Law, and Probate & Estate Planning. Trilingual services in English, Spanish, and Vietnamese.",
  "url": "https://danielalbertlaw.com",
  "telephone": "+18329303059",
  "email": "info@danielalbertlaw.com",
  "foundingDate": "2005",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "10925 Beechnut St A106",
    "addressLocality": "Houston",
    "addressRegion": "TX",
    "postalCode": "77072",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 29.6567,
    "longitude": -95.5867
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "10:00",
      "closes": "14:00"
    }
  ],
  "priceRange": "$$",
  "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Check", "Zelle"],
  "currenciesAccepted": "USD",
  "areaServed": [
    {
      "@type": "City",
      "name": "Houston",
      "sameAs": "https://en.wikipedia.org/wiki/Houston"
    },
    {
      "@type": "State",
      "name": "Texas"
    }
  ],
  "serviceArea": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": 29.7604,
      "longitude": -95.3698
    },
    "geoRadius": "50000"
  },
  "knowsLanguage": [
    {
      "@type": "Language",
      "name": "English",
      "alternateName": "en"
    },
    {
      "@type": "Language",
      "name": "Spanish",
      "alternateName": "es"
    },
    {
      "@type": "Language",
      "name": "Vietnamese",
      "alternateName": "vi"
    }
  ],
  "logo": {
    "@type": "ImageObject",
    "url": "https://danielalbertlaw.com/logo.png",
    "width": 300,
    "height": 300
  },
  "image": "https://danielalbertlaw.com/og-image.jpg",
  "sameAs": [
    "https://www.facebook.com/danielalbertlaw",
    "https://www.instagram.com/danielalbertlaw/",
    "https://www.youtube.com/@danielalbertlaw",
    "https://www.linkedin.com/company/danielalbertlaw"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Legal Services",
    "itemListElement": [
      {
        "@type": "OfferCatalog",
        "name": "Immigration Law",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Family-Based Immigration" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Employment-Based Immigration" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Asylum Services" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Citizenship & Naturalization" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Deportation Defense" } }
        ]
      },
      {
        "@type": "OfferCatalog",
        "name": "Criminal Defense",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "DWI Defense" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Drug Charges Defense" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Assault Defense" } }
        ]
      },
      {
        "@type": "OfferCatalog",
        "name": "Family Law",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Divorce" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Child Custody" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Child Support" } }
        ]
      }
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "150",
    "bestRating": "5",
    "worstRating": "1"
  }
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const generateServiceSchema = (
  serviceName: string,
  description: string,
  url: string,
  serviceType: string = "LegalService"
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": serviceName,
  "name": serviceName,
  "description": description,
  "url": url,
  "provider": {
    "@type": "LegalService",
    "@id": "https://danielalbertlaw.com/#organization",
    "name": "Daniel Albert Law Firm"
  },
  "areaServed": {
    "@type": "City",
    "name": "Houston",
    "sameAs": "https://en.wikipedia.org/wiki/Houston"
  },
  "availableChannel": {
    "@type": "ServiceChannel",
    "serviceUrl": "https://danielalbertlaw.com/appointment",
    "servicePhone": "+18329303059"
  }
});

export const generateLocalBusinessSchema = (
  area: string,
  description: string
) => ({
  "@context": "https://schema.org",
  "@type": "LegalService",
  "name": `Daniel Albert Law Firm - ${area}`,
  "description": description,
  "url": `https://danielalbertlaw.com/areas/${area.toLowerCase().replace(/\s+/g, '-')}`,
  "telephone": "+18329303059",
  "email": "info@danielalbertlaw.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "10925 Beechnut St A106",
    "addressLocality": "Houston",
    "addressRegion": "TX",
    "postalCode": "77072",
    "addressCountry": "US"
  },
  "areaServed": {
    "@type": "Place",
    "name": area
  },
  "parentOrganization": {
    "@type": "LegalService",
    "@id": "https://danielalbertlaw.com/#organization",
    "name": "Daniel Albert Law Firm"
  },
  "knowsLanguage": ["en", "es", "vi"]
});

export const generateArticleSchema = (
  title: string,
  description: string,
  publishDate: string,
  modifiedDate: string,
  url: string,
  imageUrl?: string
) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "image": imageUrl || "https://danielalbertlaw.com/og-image.jpg",
  "author": {
    "@type": "Organization",
    "name": "Daniel Albert Law Firm",
    "@id": "https://danielalbertlaw.com/#organization"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Daniel Albert Law Firm",
    "logo": {
      "@type": "ImageObject",
      "url": "https://danielalbertlaw.com/logo.png"
    }
  },
  "datePublished": publishDate,
  "dateModified": modifiedDate,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  },
  "url": url
});

export const attorneySchemaEnhanced = {
  "@context": "https://schema.org",
  "@type": "Attorney",
  "name": "Daniel Albert",
  "jobTitle": "Attorney at Law",
  "description": "Houston immigration and legal services attorney with over 20 years of experience",
  "worksFor": {
    "@type": "LegalService",
    "@id": "https://danielalbertlaw.com/#organization",
    "name": "Daniel Albert Law Firm"
  },
  "knowsLanguage": [
    { "@type": "Language", "name": "English" },
    { "@type": "Language", "name": "Spanish" },
    { "@type": "Language", "name": "Vietnamese" }
  ],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "10925 Beechnut St A106",
    "addressLocality": "Houston",
    "addressRegion": "TX",
    "postalCode": "77072",
    "addressCountry": "US"
  },
  "telephone": "+18329303059",
  "email": "info@danielalbertlaw.com",
  "url": "https://danielalbertlaw.com/about-us"
};

// Combined schema for homepage
export const homepageSchemas = [
  websiteSchema,
  organizationSchemaEnhanced,
  attorneySchemaEnhanced
];
